
import {
  computed, defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import ItemPhoto from '@/modules/drive/components/items/ItemPhoto.vue';
import WebsocketService from '@/services/WebsocketService';
import AppSkeletonMedia from '@/components/skeletons/AppSkeletonMedia.vue';
import AppSkeletonRect from '@/components/skeletons/AppSkeletonRect.vue';
import AppMessageShareSettings from '@/components/AppMessageShareSettings.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import { IMedia } from '@/types/interfaces';
import MediaService from '@/services/MediaService';
import { MEDIA_TYPES, PHOTO_VIEW_LAYOUT, SHARE_SETTINGS } from '@/types/enums';

export default defineComponent({
  components: {
    ItemPhoto,
    AppSkeletonMedia,
    AppSkeletonRect,
    AppMessageShareSettings,
  },
  emits: ['cancel', 'done'],
  props: {
    isGroup: { type: Boolean, default: false },
  },

  setup(props, context) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const messagesStore = useMessagesStore();

    const searchPhotos = ref<IMedia[]>([]);
    const dataLoading: Ref<boolean> = ref(false);

    const photos = computed(() => {
      if (driveStore.search) {
        return searchPhotos.value;
      }
      return driveStore.photos.data;
    });

    const noPhotosMessage = computed(() => {
      if (!dataLoading.value && !photos.value.length) {
        if (driveStore.search) {
          return 'No search results found';
        }
        return 'No photos here yet...';
      }
      return '';
    });

    const {
      scroller, pageIdx, isPageLoading, onScroll, onCacheScroll, setLastPage,
    } = UsePagination();

    const photoCheck = ref<any>({});
    const selectedList = computed(() => Object.keys(photoCheck.value).filter((item) => photoCheck.value[item]));

    const shareSettings: Ref<SHARE_SETTINGS[]> = ref([]);

    function doneAction() {
      if (!selectedList.value.length) {
        return;
      }
      selectedList.value.forEach((photoId) => {
        WebsocketService.sendMedia(messagesStore!.currentConversation!.info.id, photoId, props.isGroup, shareSettings.value);
      });
      context.emit('done');
    }

    async function fetchCacheData() {
      return driveStore.fetchPhotos()
        .then((response) => {
          driveStore.photoList = driveStore.photos.data;
          return response;
        });
    }

    async function fetchSearchData() {
      return MediaService.getMediaByType([MEDIA_TYPES.PHOTO, MEDIA_TYPES.VIDEO], pageIdx.value, driveStore.search)
        .then((res) => {
          if (pageIdx.value === 0) {
            searchPhotos.value = res.results.data.items;
          } else {
            searchPhotos.value.push(...res.results.data.items);
          }
          driveStore.photoList = photos.value;
          return res;
        });
    }

    function handleScroll() {
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, driveStore.photos.lastPage, driveStore.photos.page);
      }
    }

    function updateShareSettings(event) {
      shareSettings.value = [
        event.allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        event.allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        event.allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    }

    function selectPhoto(id: string) {
      photoCheck.value = { ...photoCheck.value, [id]: !photoCheck.value[id] };
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 300);
    });

    onMounted(() => {
      driveStore.search = '';
      shareSettings.value = [
        mainStore.sharingSettings[0].allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        mainStore.sharingSettings[0].allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        mainStore.sharingSettings[0].allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    });

    return {
      scroller,
      handleScroll,
      driveStore,
      photos,
      dataLoading,
      selectedList,
      photoCheck,
      isPageLoading,
      doneAction,
      selectPhoto,
      updateShareSettings,
      noPhotosMessage,
      PHOTO_VIEW_LAYOUT,
      shareSettings,
    };
  },
});
